/**
 * Input validation and normalization utilities for flexible user input handling.
 * Accepts various common formats and normalizes them for storage.
 */

/**
 * Normalizes a website URL to ensure it has a proper protocol.
 * Accepts: http://, https://, www., or just domain.com
 * Returns: URL with https:// prefix (or http:// if originally specified)
 * 
 * @param url - The user-entered website URL
 * @returns Normalized URL with protocol, or empty string if invalid
 */
export function normalizeWebsiteUrl(url: string): string {
  if (!url || typeof url !== 'string') return '';
  
  // Trim whitespace
  let normalized = url.trim();
  if (!normalized) return '';
  
  // If already has http:// or https://, return as-is (after trim)
  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }
  
  // If starts with www., add https://
  if (/^www\./i.test(normalized)) {
    return `https://${normalized}`;
  }
  
  // Otherwise assume it's just a domain (e.g., example.com or example.com/path)
  // Add https:// prefix
  return `https://${normalized}`;
}

/**
 * Validates a website URL with flexible input acceptance.
 * Accepts: http://example.com, https://example.com, www.example.com, example.com
 * 
 * @param url - The user-entered website URL
 * @returns true if valid, false otherwise
 */
export function isValidWebsiteUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return true; // Empty is valid (optional field)
  
  const trimmed = url.trim();
  if (!trimmed) return true; // Empty after trim is valid
  
  // Flexible regex that accepts:
  // - http:// or https:// prefix (optional)
  // - www. prefix (optional)
  // - domain with at least one dot and valid TLD
  // - optional path, query string, fragment
  const websiteRegex = /^(https?:\/\/)?(www\.)?[\w-]+(\.[\w-]+)+([\/\w\-._~:?#[\]@!$&'()*+,;=%]*)?$/i;
  
  return websiteRegex.test(trimmed);
}

/**
 * Normalizes a phone number by removing spaces, parentheses, and dashes.
 * Keeps the leading zero or country code intact.
 * 
 * @param phone - The user-entered phone number
 * @returns Normalized phone number with only digits (and optional leading +)
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone || typeof phone !== 'string') return '';
  
  // Remove all spaces, parentheses, dashes, and dots
  // Keep + at the start if present, and all digits
  return phone.replace(/[\s\-().]/g, '');
}

/**
 * Validates a phone number with flexible input acceptance.
 * Accepts formats like:
 * - 0871234567 (mobile)
 * - 087 123 4567 (mobile with spaces)
 * - (087) 123 4567 (mobile with parentheses)
 * - 087-123-4567 (mobile with dashes)
 * - (049) 952 6936 (landline with area code)
 * - 049 952 6936 (landline with spaces)
 * - +353871234567 (international format)
 * - +353 87 123 4567 (international with spaces)
 * 
 * For Irish (+353) and UK (+44) numbers.
 * 
 * @param phone - The user-entered phone number (after country code is prepended)
 * @param countryCode - The selected country code (+353 or +44)
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phone: string, _countryCode: string = '+353'): boolean {
  if (!phone || typeof phone !== 'string') return false;
  
  // Normalize the phone number first
  const normalized = normalizePhoneNumber(phone);
  
  // If the number starts with 0, remove it (will be replaced by country code)
  let digitsOnly = normalized;
  if (digitsOnly.startsWith('0')) {
    digitsOnly = digitsOnly.substring(1);
  }
  
  // Remove any existing country code prefix for validation
  if (digitsOnly.startsWith('+')) {
    // Already has country code, extract just the number part
    if (digitsOnly.startsWith('+353')) {
      digitsOnly = digitsOnly.substring(4);
    } else if (digitsOnly.startsWith('+44')) {
      digitsOnly = digitsOnly.substring(3);
    }
  }
  
  // Irish numbers (after country code): typically 7-10 digits
  // UK numbers (after country code): typically 7-10 digits
  // Allow 7-10 digits to be flexible
  const phoneDigitsRegex = /^\d{7,10}$/;
  
  return phoneDigitsRegex.test(digitsOnly);
}

/**
 * Formats a phone number for storage with country code.
 * Takes user input (possibly with spaces, parentheses, leading zero) and
 * returns a clean international format.
 * 
 * @param phone - The user-entered phone number
 * @param countryCode - The selected country code (+353 or +44)
 * @returns Formatted phone number like +353871234567
 */
export function formatPhoneForStorage(phone: string, countryCode: string = '+353'): string {
  if (!phone || typeof phone !== 'string') return '';
  
  // Normalize: remove spaces, parentheses, dashes
  let normalized = normalizePhoneNumber(phone);
  
  // If starts with 0, remove it
  if (normalized.startsWith('0')) {
    normalized = normalized.substring(1);
  }
  
  // If already has a + prefix, check if it matches expected country code
  if (normalized.startsWith('+')) {
    // Already formatted with country code
    return normalized;
  }
  
  // Prepend country code
  return `${countryCode}${normalized}`;
}

/**
 * React Hook Form compatible validation function for website URLs.
 * Use with register('website', { validate: validateWebsite })
 */
export function validateWebsite(value: string | undefined): true | string {
  if (!value || value.trim() === '') return true; // Optional field
  
  if (!isValidWebsiteUrl(value)) {
    return 'Please enter a valid website (e.g., example.com, www.example.com, or https://example.com)';
  }
  
  return true;
}

/**
 * React Hook Form compatible validation function for phone numbers.
 * Use with register('phone', { validate: (v) => validatePhone(v, countryCode) })
 */
export function validatePhone(value: string | undefined, countryCode: string = '+353'): true | string {
  if (!value || value.trim() === '') {
    return 'Phone number is required';
  }
  
  if (!isValidPhoneNumber(value, countryCode)) {
    return 'Please enter a valid phone number (e.g., 087 123 4567, (049) 952 6936)';
  }
  
  return true;
}
